import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useNotify, useRedirect } from 'react-admin';
import { Auth } from 'aws-amplify';
import { useForm, FormProvider } from 'react-hook-form';
import validator from 'validator';
import BackgroundImage from '../assets/images/login_bg.png';

const rightSx = {
  backgroundImage: `url(${BackgroundImage})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  height: '100%',
  width: '100%',
  display: 'flex',
};

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const redirect = useRedirect();

  // Create separate form instances for each step
  const emailForm = useForm();
  const resetForm = useForm();
  const notify = useNotify();

  const handleForgotPassword = async () => {
    try {
      setLoading(true);
      const { email } = emailForm.getValues();

      if(!validator.isEmail(email)) {
        notify('Invalid email format', { type: 'warning' });
        return
      }

      await Auth.forgotPassword(email)
      setUserEmail(email);
      setCodeSent(true);

      emailForm.reset();
      notify('Verification code sent to your email', { type: 'success'})
    } catch(e) {
      notify(e.message || 'Failed to send verification code', { type: 'error' })
    } finally {
      setLoading(false)
    }
  };

  const handleConfirmReset = async () => {
    try {
      setLoading(true);
      const { code, newPassword, confirmPassword } = resetForm.getValues();
      
      if (!code || !newPassword) {
        notify('Please enter verification code', { type: 'warning' });
        return;
      }
      
      if (!newPassword || !confirmPassword) { 
        notify('Please enter and confirm your new password', { type: 'warning' });
        return;
      }

      if (newPassword !== confirmPassword) {
        notify('Passwords do not match', { type: 'warning' });
        return;
      }
      
      await Auth.forgotPasswordSubmit(userEmail, code, newPassword);
      notify('Password reset successful. Please log in with your new password.', { type: 'success' });
      // Redirect to login page or reset form
      setCodeSent(false);
      setUserEmail('');
      resetForm.reset();

      redirect('/login');
    } catch (error) {
      console.error('Reset password error:', error);
      notify(error.message || 'Failed to reset password', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setCodeSent(false);
    setUserEmail('');
  };


  return (
    <Grid container height="100vh">
      <Grid
        item
        lg={4}
        md={6}
        sm={12}
        container
        justifyContent="center"
        alignItems="center"
      >
        <Box width="55%">
        {!codeSent ? (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Please enter your email address that you use as registered username.
              </Typography>
              <FormProvider {...emailForm}>
                <form
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleForgotPassword();
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="email"
                    label="User Email"
                    {...emailForm.register('email', { required: true })}
                  />
                  <LoadingButton
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="primary"
                    loading={loading}
                    sx={{ marginTop: '16px' }}
                  >
                    Send Verification Code
                  </LoadingButton>
                </form>
              </FormProvider>
            </>
          ) : (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Please check your email for the verification code and enter a new password.
              </Typography>
              <FormProvider {...resetForm}>
                <form
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleConfirmReset();
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="code"
                    label="Verification Code"
                    {...resetForm.register('code', { required: true })}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="password"
                    name="newPassword"
                    label="New Password"
                    {...resetForm.register('newPassword', { required: true })}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="password"
                    name="confirmPassword"
                    label="Confirm Password"
                    {...resetForm.register('confirmPassword', { required: true })}
                  />
                  <LoadingButton
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="primary"
                    loading={loading}
                    sx={{ marginTop: '16px' }}
                  >
                    RESET PASSWORD
                  </LoadingButton>
                  <Button
                    fullWidth
                    variant="text"
                    color="primary"
                    onClick={goBack}
                    sx={{ marginTop: '8px' }}
                  >
                    BACK
                  </Button>
                </form>
              </FormProvider>
            </>
          )}
        </Box>
      </Grid>
      <Grid item lg={8} md={6} sm={12} sx={rightSx} />
    </Grid>
  );
};

export default ForgotPassword;
export { ForgotPassword };
